import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommentSearchContent, SearchResult } from '@shared/models/search.model';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { LoadDataEvent } from '@shared/components/data-table-manager/data-table-manager.component';
import { CommentBaseComponent } from '@shared/models/comments/comment.model';
import { takeUntil } from 'rxjs/operators';
import { CommentService } from '@services/comment/comment.service';
import { Subject } from 'rxjs';
import { ConfigService } from '@services/config.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CommentDataMapper } from '@services/comment/comment-data-mapper';

@Component({
  selector: 'app-variant-step-comments',
  templateUrl: './variant-step-comments.component.html',
  styleUrls: ['./variant-step-comments.component.scss'],
  standalone: false,
})
export class VariantStepCommentsComponent extends CommentBaseComponent implements OnInit, OnDestroy {
  @Input() variant: RecipeLite;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly commentService: CommentService,
    private readonly commentMapper: CommentDataMapper,
    private readonly configService: ConfigService,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService,
  ) {
    super();
    this.DEFAULT_FILTER_CONFIG.isFieldFilterEnabled = true;
    this.displayInfo.noDataMessage = 'COMMENTS.SEARCH.NO_RESULT';
    this.defaultSortActive = 'creationDate';
    this.defaultSortDirection = 'desc';
  }

  ngOnInit(): void {
    this.commentDomainMarketLang = this.commentService.initDomainMarketLang(this.configService);
    this.createColumnConfig();
    this.createFieldFilters();
    const resolvedData = this.route.snapshot.data.commentData;
    this.domainMarketLang.domains = resolvedData?.domains;
    this.domainMarketLang.markets = resolvedData?.markets;
    this.domainMarketLang.languages = resolvedData?.languages;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadComments(event: LoadDataEvent): void {
    this.commentService
      .getCommentList(
        event.filterParams.paginator?.pageIndex ?? this.pageIndex,
        event.filterParams.paginator?.pageSize ?? this.pageSize,
        this.commentDomainMarketLang,
        event.filterParams.query,
        event.filterParams.sort,
        event.facetFilters,
        event.fieldFilters,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchResult: SearchResult<CommentSearchContent>) => {
        const { tableData, displayInfo, facets } = this.commentMapper.mapCommentData(
          searchResult,
          this.domains,
          this.markets,
          this.commentStatuses,
          this.commentReasons,
          this.commentDomainMarketLang,
          this.domainMarketLang,
          this.translateService,
        );
        this.tableData.data = tableData ?? [];
        this.displayInfo = displayInfo;
        this.facetGroups.next([{ title: 'FACETS.STANDARD', facets }]);
      });
  }

  private createColumnConfig(): void {
    this.columnConfigs = [
      {
        dataFieldName: 'content',
        name: 'comment',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_CONTENT',
        content: { type: 'string', value: (element: CommentSearchContent) => element.content },
        sortable: false,
      },
      {
        dataFieldName: 'status',
        name: 'status',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_STATUS',
        content: { type: 'string', value: (element: CommentSearchContent) => element.status, isStatus: true },
        sortable: true,
      },
      {
        dataFieldName: 'creationDate',
        name: 'creationDate',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_CREATIONDATE',
        content: { type: 'date', format: 'dd/MM/yyyy' },
        sortable: true,
      },
      {
        dataFieldName: 'author',
        name: 'creator.nickname',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_AUTHOR',
        content: { type: 'string', value: (element: CommentSearchContent) => element.author },
        sortable: true,
        linkCallback: (element: CommentSearchContent) => {
          const functionalId = element.identifier.functionalId;
          const sourceSystem = element.identifier.sourceSystem;

          window.open(`#/users/edit/${sourceSystem}/${functionalId}`, '_blank');
        },
      },
    ];
  }

  private createFieldFilters(): void {
    this.fieldFilters = [
      { field: 'recipe.id.functionalId', values: [this.variant.identifier.functionalId] },
      { field: 'recipe.id.sourceSystem.key', values: [this.variant.identifier.sourceSystem] },
      { field: 'status.key', values: ['PUBLISHED', 'REJECTED', 'PENDING'] },
    ];
  }
}
